// contexts/NotificationContext.tsx
import React, { createContext, useContext, useCallback, useState, ReactNode, useEffect } from 'react';
import { useWebSocket } from '../hooks/useWebSocket'; // your existing hook
import { ResponseTypes } from '../services/APIService';
import { useUserData } from './UserDataContext';

export interface Notification {
    id?: string;
    type: 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR';
    title: string;
    message: string;
    userId?: string[];
    timestamp?: string;
    topic?: string
}

type ResponseNotify = {
    id?: string;
    type: 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR';
    title: string;
    message: string;
    userId?: string[];
    timestamp?: string;
    topic?: string
    isSeen: boolean
}

interface NotificationContextType {
    notifications: Notification[];
    addNotification: (notification: Notification) => void;
    removeNotification: (id?: string) => void;
    clearNotifications: () => void;
    isConnected: boolean;
    unreadCount: number;
    notificationData: ResponseNotify[]
    readNotify: (id: string) => void
    deleteNotify: (id: string) => void
}

type NotifyDeliverBody = {
    notifyId: string
}

const getToken = (): string => {
    const token = localStorage.getItem("access_token");
    if (!token) {
        throw new Error("Missing access token");
    }
    return token
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const BASE_URL = process.env.REACT_APP_BASE_URL

    const { username } = useUserData()

    const [notifications, setNotifications] = useState<Notification[]>([]);

    const [notificationData, setNotificationData] = useState<ResponseNotify[]>([])
    const [unreadCount, setUnreadCount] = useState<number>(0)

    const { isConnected } = useWebSocket({
        url: process.env.REACT_APP_WEBSOCKET_URL || 'http://localhost:8081/websocket',
        onConnect: () => {
            console.log('✅ WebSocket connected')
            getNotifications()
        },
        onDisconnect: () => console.log('❌ WebSocket disconnected'),
        onMessage: (message) => {

            addNotification(message);

            const isForEveryone = !message.userId || message.userId.length === 0;
            //console.log('🔔 is for every one:', isForEveryone);
            const isForMe = message.userId?.includes(username);
            //console.log('🔔 is for me :', isForMe, username);

            if (isForEveryone || isForMe) 
            {
                //console.log('🔔 deliver show: ', message.id);
                updateDelivery(message.id || '');
            }
            
            console.log('🔔 New notification received:', message);
        },
    });

    const addNotification = useCallback((notification: Notification) => {
        setNotifications(prev => {
            // Avoid duplicates by id if provided
            if (notification.id && prev.some(n => n.id === notification.id)) {
                return prev;
            }
            return [{ ...notification, timestamp: notification.timestamp || new Date().toISOString() }, ...prev].slice(0, 50); // keep last 50
        });
    }, []);

    const updateDelivery = async (id: string) => {
        const body: NotifyDeliverBody = {
            notifyId: id
        }

        const URL: string = `${BASE_URL}/notify/api/v1/receive-notification`

        const HEADER: RequestInit = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getToken()}`
            },
            body: JSON.stringify(body)
        }

        const response = await fetch(URL, HEADER)
        const responseData = await response.json() as ResponseTypes

        if (response.ok)
        {
            setNotificationData(await responseData.data as ResponseNotify[])
        }
    }

    const getNotifications = async () => {

        const URL: string = `${BASE_URL}/notify/api/v1/get-notification`

        const HEADER: RequestInit = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getToken()}`
            }
        }

        const response = await fetch(URL, HEADER)
        const responseData = await response.json() as ResponseTypes

        if (response.ok)
        {
            setNotificationData(await responseData.data as ResponseNotify[])
        }
    }

    const readNotify = async (id: string) => {
        const URL: string = `${BASE_URL}/notify/api/v1/seen-notification?notifyId=${id}`

        const HEADER: RequestInit = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getToken()}`
            },
        }

        const response = await fetch(URL, HEADER)
        const responseData = await response.json() as ResponseTypes

        if (response.ok)
        {
            setNotificationData(responseData.data as ResponseNotify[])
        }
    }

    const deleteNotify = async (id: string) => {
        const URL: string = `${BASE_URL}/notify/api/v1/delete-notification?notifyId=${id}`

        const HEADER: RequestInit = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getToken()}`
            },
        }

        const response = await fetch(URL, HEADER)
        const responseData = await response.json() as ResponseTypes

        if (response.ok)
        {
            setNotificationData(await responseData.data as ResponseNotify[])
        }
    }

    const removeNotification = useCallback((id?: string) => {
        if (!id) return;
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    const clearNotifications = useCallback(() => {
        setNotifications([]);
    }, []);

    useEffect(() => {
        if (notificationData === undefined || notificationData === null) return
        const unCount = notificationData.filter(item => item.isSeen === false).length || 0;
        setUnreadCount(unCount)
    }, [notificationData])

    return (
        <NotificationContext.Provider value={{
            notifications,
            addNotification,
            removeNotification,
            clearNotifications,
            isConnected,
            unreadCount,
            notificationData,
            readNotify,
            deleteNotify
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within NotificationProvider');
    }
    return context;
};